
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { Icon, IconSet } from "@/types/icons";
import { configStore } from "@/store/store";
import IconUpload from "./IconUpload.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import SearchInput from "@/components/Reusable/CustomInputs/SearchInput.vue";
import IconComponent from "@/components/UI/Icon.vue";
import Dialog from "@/components/UI/Dialog.vue";
import { deleteIconSet, findIcons, getIconSets } from "@/lib/icons/ajax";
import { debounce } from "lodash";
import Loading from "@/components/Reusable/Loading.vue";
import LoadingSpinner from "@/components/Reusable/LoadingSpinner.vue";
import InfiniteScroll from "@/directives/InfiniteScroll";

enum Mode {
  Upload = "upload",
  Browse = "browse"
}

type VirtualIconSet = Omit<IconSet, "slug"> & {
  virtual: true;
  virtualType: "all" | "suggested";
};

@Component({
  components: {
    LoadingSpinner,
    Loading,
    Dialog,
    IconComponent,
    SearchInput,
    TextInput,
    IconUpload
  },
  directives: {
    InfiniteScroll
  }
})
export default class IconChooserDialog extends Vue {
  @Prop({ default: false })
  open!: boolean;

  @Prop({
    default() {
      return [];
    }
  })
  suggestions!: string[];

  mode: Mode = Mode.Browse;
  searchString: string | null = "";

  activeIconSetIndex = 0;
  isIconSetDeletionInProgress = false;

  icons: Icon[] = [];
  areIconsLoading = false;

  /* We change the key whenever upload mode is enabled
  to get vue to reset the IconUpload so that when the user
  leaves, if they come back the IconUpload is  cleared */
  uploadKey = "69";

  get placeholder() {
    return `Search in "${this.activeIconSet.name}"`;
  }

  get activeIconSet(): IconSet | VirtualIconSet {
    return this.allIconSets[this.activeIconSetIndex];
  }

  get allIconSets(): Array<IconSet | VirtualIconSet> {
    return [...this.virtualIconSets, ...this.storedIconSets];
  }

  get virtualIconSets(): Array<VirtualIconSet> {
    const extra: Array<VirtualIconSet> = [
      { name: "All Icons", virtual: true, virtualType: "all" }
    ];

    if (this.suggestions.length) {
      extra.unshift({
        name: "Suggested Icons",
        virtual: true,
        virtualType: "suggested"
      });
    }

    return extra;
  }

  get storedIconSets(): Array<IconSet> {
    return configStore.iconSets;
  }

  async mounted() {
    await this.retrieveIconSets();
    await this.findIcons();
  }

  throttledHandleSearchStringChange = debounce(
    this.handleSearchStringChange,
    600
  );

  async handleSearchStringChange(newVal: string | null) {
    this.searchString = newVal;
    await this.findIcons();
  }

  handleInfiniteScroll() {
    if (this.areIconsLoading) {
      return;
    }

    this.findIcons(true);
  }

  scrollToTop() {
    const container = this.$refs.scrollable as HTMLElement;
    container.scrollTop = 0;
  }

  async retrieveIconSets() {
    configStore.setIconSets([]);

    const maybeIconSets = await getIconSets();

    if (maybeIconSets !== null) {
      configStore.setIconSets(maybeIconSets);
    }
  }

  async findIcons(append = false) {
    const chooseIconSetSlugSearchParameter = (): string | undefined => {
      if ("virtual" in this.activeIconSet) {
        return undefined;
      }
      return this.activeIconSet.slug;
    };

    if (!append) {
      this.icons = [];
    }

    this.areIconsLoading = true;

    const maybeIcons = await findIcons({
      searchTerm:
        this.searchString !== null && this.searchString.trim().length
          ? this.searchString.trim()
          : undefined,
      iconSetSlug: chooseIconSetSlugSearchParameter(),
      offset: append ? this.icons.length : undefined,
      suggestions:
        "virtual" in this.activeIconSet &&
        this.activeIconSet.virtualType === "suggested"
          ? this.suggestions
          : undefined
    });

    this.areIconsLoading = false;

    if (maybeIcons === null) {
      this.icons = [];
    } else {
      if (append) {
        this.icons.push(...maybeIcons);
      } else {
        this.icons = maybeIcons;
      }
    }
  }

  setActiveIconSetIndex(index: number) {
    this.activeIconSetIndex = index;
    this.mode = Mode.Browse;
    this.scrollToTop();
    this.findIcons();
  }

  setUploadMode() {
    this.activeIconSetIndex = -1;
    this.mode = Mode.Upload;
    this.uploadKey = (Math.random() * 100).toString();
  }

  async uploadComplete(modifiedIconSetSlug: IconSet["slug"]) {
    await this.retrieveIconSets();

    const modifiedIconSetIndex = this.allIconSets.findIndex(
      iconSet => !("virtual" in iconSet) && iconSet.slug === modifiedIconSetSlug
    );

    this.setActiveIconSetIndex(
      modifiedIconSetIndex === -1 ? 0 : modifiedIconSetIndex
    );
  }

  async deleteIconSet(index: number) {
    if (index < this.virtualIconSets.length) return;

    const storedIconSetIndex = index - this.virtualIconSets.length;

    const storedIconSet = this.storedIconSets[storedIconSetIndex];

    const confirmed = confirm(
      `Are you sure you want to delete ${storedIconSet.name}?`
    );

    if (!confirmed) {
      return;
    }

    this.isIconSetDeletionInProgress = true;
    await deleteIconSet(storedIconSet.slug);
    await this.retrieveIconSets();
    this.activeIconSetIndex = 0;
    this.isIconSetDeletionInProgress = false;

    await this.findIcons();
  }

  formatName(icon: Icon) {
    return icon.name.replace(/icon-|\./g, "").replace(/-/g, " ");
  }
}
